/**
 * Home (Oct 2026 revamp, after looking at Granted's member portal).
 *
 * Granted's home is a short list of things you can do, then your cases,
 * then a few common questions. It never feels like a dashboard. Ours does
 * the same:
 *   1. "How can we help?" with one big card (check a bill) and two small
 *      ones (add family, ask us)
 *   2. your cases, each with a one-word status pill
 *   3. common questions that open right here, with the same answers as
 *      the FAQ on mediloop.ai
 * Rendered at / (see DashboardRedirect, which still honours
 * ?case= deep links).
 */

import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ChevronRight,
  Clock,
  CreditCard,
  Hand,
  MessageCircle,
  ShieldCheck,
  Send,
  UserPlus,
  Wallet,
} from 'lucide-react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../hooks/useAuth';
import { useUnreadCounts } from '../hooks/useBillRealtime';
import { phStartNewCaseClicked } from '../lib/posthog';
import { useAsk } from '../lib/ask';
import type { Bill } from '../types/bill';
import { caseStatusLine, caseTitle, casePill, isBillClosed } from '../lib/case-summary';
import { loadBillDraft, type BillDraft } from '../lib/freeAccount';
import ReferralPanel from '../components/ReferralPanel';
import '@styles/pages/home.css';

/* Copied word for word from apps/web/src/lib/faq-content.js so the app
   and the site never give two different answers. If one changes there,
   change it here too. */
const QUESTIONS = [
  {
    id: 'time',
    Icon: Clock,
    q: 'How long does it take?',
    a: 'Most cases take 2 to 4 weeks. Some take longer, usually because a provider is slow to reply. You can follow every step in your account, and we email you when something changes.',
  },
  {
    id: 'keep-paying',
    Icon: Wallet,
    q: 'Should I keep paying the bill while you work on it?',
    a: "Hold off if you can. Paying the bill, or agreeing a payment plan, while we're working on it makes it much harder to bring the amount down, and the money-back guarantee no longer applies. If a due date is close, tell us and we'll ask the provider to put the account on hold while we review it. We can't promise every provider will.",
  },
  {
    id: 'refund',
    Icon: ShieldCheck,
    q: "What if the hospital won't lower my bill?",
    a: "You get your money back. On Pay Per Bill, if we don't lower the bill by at least $129, the $129 is refunded in full the day your case closes. On the Annual Plan, if your bills aren't lowered by at least $499 combined, the full $499 is refunded. For the guarantee to apply, don't pay the bill or agree a payment with your provider while we're working on it, and reply within 14 days when we ask you for something.",
  },
  {
    id: 'hands-on',
    Icon: Hand,
    q: 'How hands-on do I need to be?',
    a: "Barely. Upload your bill and sign a HIPAA authorization so we're allowed to talk to your provider for you. After that we only get in touch if we need a document or your OK on something.",
  },
  {
    id: 'collections',
    Icon: CreditCard,
    q: "Can my bill go to collections while you're working on it?",
    a: "It can, so we don't let a bill sit. We tell your provider the bill is under review and ask them to hold it. Some situations also make them pause by law, for example once you've applied for financial assistance at a nonprofit hospital. If a collections notice arrives while we're on your case, send it to us straight away.",
  },
] as const;

function filedOn(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
}

export default function Home() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const ask = useAsk();

  const [bills, setBills] = useState<Bill[]>([]);
  const [firstName, setFirstName] = useState('');
  const [plan, setPlan] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [openQ, setOpenQ] = useState<string | null>(null);
  /* A bill uploaded on a free account that hasn't been started yet. It
     isn't a bills row, so without this the page said "no cases" to someone
     who had just uploaded one (Patty, 13 Oct 2026). */
  const [draft, setDraft] = useState<BillDraft | null>(null);
  /* Open asks from a specialist ("Upload your EOB"), by case id. */
  const [asks, setAsks] = useState<Record<string, string>>({});

  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    (async () => {
      try {
        const [billsRes, profileRes, subRes, parked] = await Promise.all([
          supabase
            .from('bills')
            .select('*')
            .eq('user_id', user.id)
            .order('created_at', { ascending: false }),
          supabase
            .from('profiles')
            .select('first_name')
            .eq('id', user.id)
            .maybeSingle(),
          supabase
            .from('subscriptions')
            .select('plan')
            .eq('user_id', user.id)
            .maybeSingle(),
          loadBillDraft(user.id),
        ]);
        if (cancelled) return;
        setDraft(parked);
        if (billsRes.data) setBills(billsRes.data);
        if (profileRes.data?.first_name) {
          const n = profileRes.data.first_name;
          setFirstName(n.charAt(0).toUpperCase() + n.slice(1));
        }
        if (subRes.data) setPlan(subRes.data.plan);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [user]);

  const billIds = useMemo(() => bills.map((b) => b.id), [bills]);

  useEffect(() => {
    if (!billIds.length) return;
    let cancelled = false;
    supabase
      .from('customer_requests')
      .select('bill_id, title, status, requested_at')
      .in('bill_id', billIds)
      .in('status', ['open', 'paused'])
      .order('requested_at', { ascending: false })
      .then(({ data }) => {
        if (cancelled || !data) return;
        const map: Record<string, string> = {};
        for (const r of data as { bill_id: string; title: string }[]) {
          if (!map[r.bill_id]) map[r.bill_id] = r.title;
        }
        setAsks(map);
      });
    return () => { cancelled = true; };
  }, [billIds]);

  /* Cases that need the customer go first, then open ones, then closed. */
  const sortedBills = useMemo(() => {
    const rank = (b: Bill) => (asks[b.id] || casePill(b).tone === 'you' ? 0 : isBillClosed(b) ? 2 : 1);
    return [...bills].sort((a, b) => rank(a) - rank(b));
  }, [bills, asks]);
  const needsYou = sortedBills.filter((b) => asks[b.id] || casePill(b).tone === 'you');
  const { unreadCounts } = useUnreadCounts(billIds);

  /* Secured savings only: sum over CLOSED cases. Counting active
     partial wins would claim money that isn't locked in yet. */
  const totalSaved = bills
    .filter(isBillClosed)
    .reduce((sum, b) => sum + (Number(b.amount_saved) || 0), 0);

  const startCase = (source: string) => {
    phStartNewCaseClicked({ source });
    navigate('/add-bill');
  };

  const hasCases = bills.length > 0 || Boolean(draft);
  /* A free account (no plan yet) gets its own version of this page
     (2026-10-27): the bill they've parked and what starting it gets them,
     a way to message us, and plain words about price. Family needs a paid
     case, so that card waits until they have one. */
  const isFree = !loading && !plan;
  /* Someone with a bill parked and nothing started: the big card points
     at that bill instead of asking for another one. */
  const draftOnly = Boolean(draft) && bills.length === 0;

  return (
    <div className="hm">
      <h1 className="ml-title hm-title">
        {firstName ? `Hi ${firstName}, how can we help?` : 'How can we help?'}
      </h1>

      {/* ── Needs you ── Anything a specialist is waiting on goes first,
          above everything else on the page. */}
      {!loading && needsYou.length > 0 && (
        <button
          type="button"
          className="hm-attn"
          onClick={() => navigate(`/case/${needsYou[0].id}`)}
        >
          <span className="hm-attn-dot" aria-hidden />
          <span className="hm-attn-text">
            <span className="hm-attn-title">
              {asks[needsYou[0].id] ?? 'We need something from you'}
            </span>
            <span className="hm-attn-sub">
              {caseTitle(needsYou[0])}
              {needsYou.length > 1 ? ` and ${needsYou.length - 1} more case${needsYou.length > 2 ? 's' : ''}` : ''}
              {' '}is waiting on you
            </span>
          </span>
          <span className="hm-attn-cta">Open</span>
        </button>
      )}

      {/* ── What you can do ── */}
      <div className="hm-actions">
        <div className="ml-card hm-main">
          <img src="/images/mascot/fox-reading-bill.png" alt="" className="hm-main-art" />
          <div className="hm-main-text">
            <p className="hm-main-title">
              {draftOnly ? 'Your bill is uploaded' : isFree ? 'Get a free first look at your bill' : 'Get help with a bill'}
            </p>
            <p className="hm-main-body">
              {draftOnly
                ? 'Open it to see what we found and start the case when you’re ready.'
                : isFree
                ? 'Upload it and see what we find. Nothing is charged until you start a case.'
                : 'Upload it and see what we find. A specialist takes it from there.'}
            </p>
          </div>
          {draftOnly ? (
            <button type="button" className="ml-btn ml-btn--bright" onClick={() => navigate('/case/draft')}>
              Open my bill
            </button>
          ) : (
            <button type="button" className="ml-btn ml-btn--bright" onClick={() => startCase('home_main')}>
              Upload a bill
            </button>
          )}
        </div>

        {isFree && (
          <div className="ml-card hm-free">
            <p className="hm-free-title">What happens when you start a case</p>
            <ol className="hm-free-steps">
              <li>
                <span className="num">1</span>
                <div>
                  <strong>We check every line</strong>
                  <p>Our AI-powered investigation goes through the bill, and a specialist reviews what it finds.</p>
                </div>
              </li>
              <li>
                <span className="num">2</span>
                <div>
                  <strong>We deal with the hospital</strong>
                  <p>We get the itemized bill and negotiate the price. You don't make any calls.</p>
                </div>
              </li>
              <li>
                <span className="num">3</span>
                <div>
                  <strong>Money back if we can't save you $129</strong>
                  <p>One flat fee of $129, refunded in full the same day if we don't save you at least that.</p>
                </div>
              </li>
            </ol>
            <div className="hm-free-foot">
              <button
                type="button"
                className="ml-btn ml-btn--ink"
                onClick={() => (draft ? navigate('/case/draft') : startCase('home_free_steps'))}
              >
                {draft ? 'Start my case' : 'Upload a bill for free'}
              </button>
              <button type="button" className="ml-link" onClick={() => navigate('/plan')}>
                See plans
              </button>
            </div>
          </div>
        )}

        <div className="hm-minis">
          {isFree ? (
            <button
              type="button"
              className="ml-card hm-mini"
              onClick={() => (draft ? navigate('/case/draft') : ask.talkToPerson())}
            >
              <span className="hm-mini-icon"><Send size={17} strokeWidth={1.75} /></span>
              <span className="hm-mini-text">
                <span className="hm-mini-title">Message the team</span>
                <span className="hm-mini-body">A person answers your questions before you start.</span>
              </span>
              <ChevronRight size={18} className="hm-chev" aria-hidden />
            </button>
          ) : (
          <button type="button" className="ml-card hm-mini" onClick={() => navigate('/family-members')}>
            <span className="hm-mini-icon"><UserPlus size={18} strokeWidth={1.75} /></span>
            <span className="hm-mini-text">
              <span className="hm-mini-title">Add someone in your family</span>
              <span className="hm-mini-body">We can work on their bills too.</span>
            </span>
            <ChevronRight size={18} className="hm-chev" aria-hidden />
          </button>
          )}
          <button type="button" className="ml-card hm-mini" onClick={() => ask.open()}>
            <span className="hm-mini-icon"><MessageCircle size={18} strokeWidth={1.75} /></span>
            <span className="hm-mini-text">
              <span className="hm-mini-title">Ask Agent Loop</span>
              <span className="hm-mini-body">Get an answer now about your cases or a bill.</span>
            </span>
            <ChevronRight size={18} className="hm-chev" aria-hidden />
          </button>
        </div>
      </div>

      {/* ── Your cases ── */}
      {loading ? (
        <section className="hm-section" aria-busy="true">
          <p className="ml-section-label">Your cases</p>
          <div className="hm-skel" />
        </section>
      ) : hasCases ? (
        <section className="hm-section">
          <div className="hm-section-head">
            <p className="ml-section-label">Your cases</p>
            {totalSaved > 0 && (
              <p className="hm-saved">
                <span className="num">${totalSaved.toLocaleString()}</span> saved so far
              </p>
            )}
          </div>

          <div className="hm-cases">
            {draftOnly && draft && (
              <button type="button" className="ml-card hm-case" onClick={() => navigate('/case/draft')}>
                <span className="hm-case-main">
                  <span className="hm-case-title">
                    {draft.provider_name || draft.file_name || 'Your bill'}
                  </span>
                  <span className="hm-case-line">Uploaded, waiting for you to start</span>
                </span>
                <span className="ml-pill ml-pill--draft">Not started</span>
                <ChevronRight size={18} className="hm-chev" aria-hidden />
              </button>
            )}
            {sortedBills.slice(0, 5).map((bill) => {
              const pill = casePill(bill);
              const unread = unreadCounts[bill.id] || 0;
              return (
                <button
                  key={bill.id}
                  type="button"
                  className="ml-card hm-case"
                  onClick={() => navigate(`/case/${bill.id}`)}
                >
                  <span className="hm-case-main">
                    <span className="hm-case-title">
                      {caseTitle(bill)}
                      {unread > 0 && (
                        <span className="hm-unread" title={`${unread} new`}>
                          {unread} new
                        </span>
                      )}
                    </span>
                    <span className="hm-case-line">
                      {filedOn(bill.created_at)}
                      <span aria-hidden> · </span>
                      {caseStatusLine(bill)}
                    </span>
                  </span>
                  <span className={`ml-pill ml-pill--${pill.tone}`}>{pill.label}</span>
                  <ChevronRight size={18} className="hm-chev" aria-hidden />
                </button>
              );
            })}
          </div>
          {bills.length > 5 && (
            <button type="button" className="ml-link hm-all" onClick={() => navigate('/bills')}>
              See all {bills.length} cases
            </button>
          )}
        </section>
      ) : null}

      {/* The referral box (Give $40, get $40). It used to live in the old
          sidebar, and the revamp had left it only at the bottom of Plan.
          Renders nothing until the account has a referral code. */}
      {!loading && (
        <div className="hm-refer">
          <ReferralPanel />
        </div>
      )}

      {/* ── Common questions ── */}
      <section className="hm-section">
        <p className="ml-section-label">Common questions</p>
        <div className="hm-faq">
          {QUESTIONS.map(({ id, Icon, q, a }) => {
            const open = openQ === id;
            return (
              <div key={id} className={`hm-q ${open ? 'open' : ''}`}>
                <button
                  type="button"
                  className="hm-q-btn"
                  aria-expanded={open}
                  onClick={() => setOpenQ(open ? null : id)}
                >
                  <span className="hm-q-icon"><Icon size={15} strokeWidth={1.75} /></span>
                  <span className="hm-q-text">{q}</span>
                  <ChevronRight size={18} className="hm-q-chev" aria-hidden />
                </button>
                {open && <p className="hm-q-answer">{a}</p>}
              </div>
            );
          })}
        </div>
        <p className="hm-more">
          Something else?{' '}
          <button type="button" className="ml-link" onClick={() => ask.open()}>
            Ask Agent Loop
          </button>
        </p>
      </section>

      {isFree && (
        <footer className="hm-foot">
          <p>
            You're on a free account. Uploading a bill and the first look are free, and nothing
            is charged until you start a case.{' '}
            <button type="button" className="ml-link" onClick={() => navigate('/plan')}>
              See plans
            </button>
          </p>
        </footer>
      )}

      {/* ── Plan lines, kept quiet at the bottom ── */}
      {!loading && bills.length > 0 && (
        <footer className="hm-foot">
          {plan === 'annual' ? (
            <p>
              Your plan covers up to 6 cases a year. Send the next bill whenever
              one lands.
            </p>
          ) : plan === 'eob' ? (
            <p>
              Ready for a full case? Your EOB review fee counts toward it.{' '}
              <button type="button" className="ml-link" onClick={() => navigate('/plan')}>
                See plans
              </button>
            </p>
          ) : (
            <p>
              Another bill is $129 with the same guarantee, or the annual plan
              covers up to 6 a year for $499 (what you've paid counts toward it).{' '}
              <button type="button" className="ml-link" onClick={() => navigate('/plan')}>
                See plans
              </button>
            </p>
          )}
        </footer>
      )}

    </div>
  );
}
